import {
  computePipeSectionMAWP,
  computePipeSectionThicknessFromMAWP,
} from 'features/indicationTriage/cards/ffsCalc/calculations/mawp/calculatePipingMawp'
import {
  computePVCylindricalShellMAWP,
  computePVCylindricalShellThicknessFromMAWP,
} from 'features/indicationTriage/cards/ffsCalc/calculations/mawp/calculatePVMawp'
import {
  Sub,
  Sup,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { LABEL_RSFA } from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import {
  transformJsonMeasurementObject,
  transformMeasurementObject,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import {
  FfsLevel,
  FfsPart,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { MawpCalcThicknessOption } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import {
  DEFAULT_INDETERMINATE_RESULT,
  FFS_RESULT_CASES_PART_4,
  FFS_RESULT_CASES_PART_5,
  FfsAnalysisResult,
  FfsPartFiveResultIds,
  FfsPartFourResultIds,
  INDETERMINATE_FFS_RESULT_ID,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsResultScenarios'
import { FfsPartFourCalculatedValues } from 'features/indicationTriage/cards/ffsCalc/part4/FfsCalculatedValuesSection'
import type { useFfsPartFourDataValues } from 'features/indicationTriage/cards/ffsCalc/part4/FfsDataValuesSection'
import { FinalAcceptabilityCriteriaCheckResult } from 'features/indicationTriage/cards/ffsCalc/part4/FfsFinalAcceptabilityCriteriaSection'
import { FfsThicknessCriteriaSectionProps } from 'features/indicationTriage/cards/ffsCalc/part4/FfsThicknessCriteriaSection'
import type { useFfsPartFiveDataValues } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsDataValuesSection'
import { FfsFinalAcceptabilityCriteraSectionValues } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFinalAcceptabilityCriteraSection'
import { FlawSizeCriteriaCheckResult } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFlawSizeCriteriaSection'
import filter from 'lodash/filter'
import isNil from 'lodash/isNil'
import minBy from 'lodash/minBy'
import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
} from 'react'
import {
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import {
  FfsLtaReport,
  FfsPartFiveFinalAcceptabilityCriteria,
  FfsPartFiveReport,
  FfsPartFiveStoredFlawSizeCriteria,
  FfsPartFourReport,
  FfsPartFourStoredCalculatedValues,
  FfsPartFourStoredThicknessCriteria,
} from './ffsReports'

// - - - - - - - - - - - - - - - - Data Values - - - - - - - - - - - - - - - -
export interface NormalizedFfsDataValues {
  loading: boolean
  tmm: GeckoMeasurement | null
  trd: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  longitudinalFlawLength: GeckoMeasurement | null
  latitudinalFlawLength: GeckoMeasurement | null
  innerDiameter: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  nominal: GeckoMeasurement | null
  corrosionOnInnerSurface: boolean
  setCorrosionOnInnerSurface: Dispatch<SetStateAction<boolean>>
  t_ml: GeckoMeasurement | null
  d_ml: GeckoMeasurement | null
  lambda: GeckoMeasurement | null
}

type PartFourDataValues = ReturnType<typeof useFfsPartFourDataValues>
type PartFiveDataValues = ReturnType<typeof useFfsPartFiveDataValues>

interface NormalizeDataValuesArgs {
  partFour: PartFourDataValues
  partFive: PartFiveDataValues
}

interface ComputeReratedMawpArgs {
  mawp_c: GeckoMeasurement | null
  mawp_l: GeckoMeasurement | null
  rsf: GeckoMeasurement | null
  rsfa: GeckoMeasurement | null
  ffsLevel: FfsLevel
}

interface ComputeAnalysisResultArgs {
  mawpr: GeckoMeasurement | null
  mawpDesign: GeckoMeasurement | null
  finalAcceptabilityCriteriaCheckProps?: FinalAcceptabilityCriteriaCheckResult
  rsf?: GeckoMeasurement | null
  flawSizeCheckResult?: FlawSizeCriteriaCheckResult
}

const WORKING_UNIT_PRESS = GeckoMeasurementUnit.Megapascals

function normalizePartFourDataValues(
  data: PartFourDataValues
): NormalizedFfsDataValues {
  const { t_ml, d_ml, ...common } = data

  return {
    ...common,
    t_ml,
    d_ml,
    lambda: null,
  }
}

function normalizePartFiveDataValues(
  data: PartFiveDataValues
): NormalizedFfsDataValues {
  const { lambda, ...common } = data

  return {
    ...common,
    t_ml: null,
    d_ml: null,
    lambda,
  }
}

type NormalizeDataValuesFn = (
  args: NormalizeDataValuesArgs
) => NormalizedFfsDataValues | null
type ReratedTooltipRenderer = (ffsLevel: FfsLevel) => ReactNode
type TooltipRenderer = () => ReactNode
type ComputeAnalysisResultFn = (
  args: ComputeAnalysisResultArgs
) => FfsAnalysisResult

const NORMALIZE_DATA_VALUES_BY_PART: Record<FfsPart, NormalizeDataValuesFn> = {
  [FfsPart.PART_4]: ({ partFour }) => normalizePartFourDataValues(partFour),
  [FfsPart.PART_5]: ({ partFive }) => normalizePartFiveDataValues(partFive),
}

// - - - - - - - - - - - - - - - - MAWP - - - - - - - - - - - - - - - -
const MAWP_CALC_TYPE_OPTIONS: Partial<
  Record<GeckoComponentModelType, ReadonlyArray<CalcTypeOption>>
> = {
  [GeckoComponentModelType.PressureVesselShell]: [
    {
      value: 'thinWall',
      label: 'Thin Wall',
    },
    {
      value: 'thickWall',
      label: 'Thick Wall',
    },
  ],
}

/**
 * Get the MAWP calculation type options for a component type.
 */
function getMawpCalcTypeOptions(
  componentType: GeckoComponentModelType | undefined
): readonly CalcTypeOption[] {
  if (!componentType) {
    return []
  }
  return MAWP_CALC_TYPE_OPTIONS[componentType] ?? []
}

const MAWP_CALC_THICKNESS_OPTIONS_BY_PART: Record<
  FfsPart,
  readonly ThicknessOption[]
> = {
  [FfsPart.PART_4]: [
    {
      value: 't_am',
      label: (
        <>
          t<Sub>am</Sub>
        </>
      ),
    },
    {
      value: 't_mm',
      label: (
        <>
          t<Sub>mm</Sub>
        </>
      ),
    },
  ],
  [FfsPart.PART_5]: [
    {
      value: 't_c',
      label: (
        <>
          t<Sub>c</Sub>
        </>
      ),
    },
  ],
} as const

type ComputeReratedMawpFn = (
  args: ComputeReratedMawpArgs
) => GeckoMeasurement | null

const COMPUTE_RERATED_MAWP_BY_PART: Record<FfsPart, ComputeReratedMawpFn> = {
  [FfsPart.PART_4]: ({ mawp_c, mawp_l, rsf, rsfa, ffsLevel }) => {
    if (isNil(mawp_c) && isNil(mawp_l)) {
      return null
    }

    const minMawp =
      minBy([mawp_c, mawp_l], (measurement) => measurement?.num) ?? null

    if (isNil(minMawp)) {
      return null
    }

    switch (ffsLevel) {
      case '1':
        return minMawp
      case '2':
        if (isNil(rsf) || isNil(rsfa)) {
          return null
        }
        return GeckoMeasurement.CreateFromValue(
          minMawp.toNumber(WORKING_UNIT_PRESS) /
            rsfa.toNumber(GeckoMeasurementUnit.Ratio),
          WORKING_UNIT_PRESS
        )
      default:
        return null
    }
  },
  [FfsPart.PART_5]: ({ mawp_c, mawp_l, rsf, rsfa }) => {
    const minMawp =
      minBy([mawp_c, mawp_l], (measurement) => measurement?.num) ?? null

    if (isNil(minMawp) || isNil(rsf) || isNil(rsfa)) {
      return null
    }

    return GeckoMeasurement.CreateFromValue(
      minMawp.toNumber(WORKING_UNIT_PRESS) *
        (rsf.toNumber(GeckoMeasurementUnit.Ratio) /
          rsfa.toNumber(GeckoMeasurementUnit.Ratio)),
      WORKING_UNIT_PRESS
    )
  },
}

const CircMawpTooltipBase: FC = () => (
  <>
    MAWP using circumferential stress
    <TooltipDivider />
    Formula: MAWP = (2 × S × E × t) / (D - 2 × Y × t)
  </>
)

const LongMawpTooltipBase: FC = () => (
  <>
    MAWP using longitudinal stress
    <TooltipDivider />
    Formula: MAWP = (4 × S × E × t) / (D - 4 × Y × t)
  </>
)

const RENDER_RERATED_TOOLTIP_BY_PART: Record<FfsPart, ReratedTooltipRenderer> =
  {
    [FfsPart.PART_4]: (ffsLevel) => {
      const common = <>Re-rated MAWP</>
      return ffsLevel === '1' ? (
        <>
          {common}
          <TooltipDivider />
          min(MAWP<Sup>C</Sup>, MAWP<Sup>L</Sup>)
        </>
      ) : (
        <>
          {common}
          <TooltipDivider />
          min(MAWP<Sup>C</Sup>, MAWP<Sup>L</Sup>) / RSF<Sub>a</Sub>
        </>
      )
    },
    [FfsPart.PART_5]: () => {
      const common = <>Re-rated MAWP</>
      return (
        <>
          {common}
          <TooltipDivider />
          MAWP<Sub>tc</Sub> × (RSF / {LABEL_RSFA})
        </>
      )
    },
  }

const RENDER_CIRC_MAWP_TOOLTIP_BY_PART: Record<FfsPart, TooltipRenderer> = {
  [FfsPart.PART_4]: () => (
    <>
      <CircMawpTooltipBase />
      <TooltipDivider />
      Based on t = (t<Sub>selected</Sub>
      <Sup>c</Sup> - FCA<Sub>ml</Sub>)
    </>
  ),
  [FfsPart.PART_5]: () => (
    <>
      <CircMawpTooltipBase />
      <TooltipDivider />
      Based on t = t<Sub>c</Sub>
    </>
  ),
}

const RENDER_LONG_MAWP_TOOLTIP_BY_PART: Record<FfsPart, TooltipRenderer> = {
  [FfsPart.PART_4]: () => (
    <>
      <LongMawpTooltipBase />
      <TooltipDivider />
      Based on t = (t<Sub>selected</Sub>
      <Sup>l</Sup> - tsl - FCA<Sub>ml</Sub>)
    </>
  ),
  [FfsPart.PART_5]: () => (
    <>
      <LongMawpTooltipBase />
      <TooltipDivider />
      Based on t = t<Sub>c</Sub> - tsl
    </>
  ),
}

// - - - - - - - - - - - - - - - - Analysis Result - - - - - - - - - - - - - - - -
const CRITERIA_THICKNESS_THRESHOLDS: Record<GeckoComponentModelType, number> = {
  [GeckoComponentModelType.VerticalTankShell]: 2.5,
  [GeckoComponentModelType.TankFloor]: 2.5,
  [GeckoComponentModelType.TankFlatRoof]: 2.5,
  [GeckoComponentModelType.HorizontalTankShell]: 2.5,
  [GeckoComponentModelType.PipeSection]: 1.3,
  [GeckoComponentModelType.LegacyBoilerWalls]: 2.5,
  [GeckoComponentModelType.ShipDeck]: 2.5,
  [GeckoComponentModelType.BasicShipHull]: 2.5,
  [GeckoComponentModelType.Cone]: 2.5,
  [GeckoComponentModelType.LegacyCone]: 2.5,
  [GeckoComponentModelType.LaunchTube]: 2.5,
  [GeckoComponentModelType.Dome]: 2.5,
  [GeckoComponentModelType.DomeWall]: 2.5,
  [GeckoComponentModelType.FlatCircularFloor]: 2.5,
  [GeckoComponentModelType.VerticalTankFoundation]: 2.5,
  [GeckoComponentModelType.PipeElbow]: 1.3,
  [GeckoComponentModelType.PipeTJoint]: 1.3,
  [GeckoComponentModelType.PipeEccentricReducer]: 1.3,
  [GeckoComponentModelType.MachinedForgingSimple]: 2.5,
  [GeckoComponentModelType.PressureVesselShell]: 2.5,
  [GeckoComponentModelType.HemisphericalHead]: 2.5,
  [GeckoComponentModelType.TorisphericalHead]: 2.5,
  [GeckoComponentModelType.EllipsoidalHead]: 2.5,
  [GeckoComponentModelType.SphereFlatCap]: 2.5,
  [GeckoComponentModelType.HoodStraight]: 1.3,
  [GeckoComponentModelType.HoodBend]: 1.3,
}
const DEFAULT_CRITERIA_THICKNESS_THRESHOLD = 2.5

/**
 * Get the thickness threshold for thickness criteria per component type
 */
function getThicknessThreshold(
  componentType: GeckoComponentModelType | undefined
): GeckoMeasurement {
  return GeckoMeasurement.CreateFromValue(
    componentType
      ? CRITERIA_THICKNESS_THRESHOLDS[componentType]
      : DEFAULT_CRITERIA_THICKNESS_THRESHOLD,
    GeckoMeasurementUnit.Millimeters
  )
}

/**
 * Helper function to get a specific result case by key
 */
const getFfsResultCase = (key: string, ffsPart: FfsPart): FfsAnalysisResult => {
  const resultCases =
    ffsPart === FfsPart.PART_4
      ? FFS_RESULT_CASES_PART_4
      : FFS_RESULT_CASES_PART_5
  const resultCase =
    resultCases.find((resultCase) => resultCase.resultId === key) ??
    DEFAULT_INDETERMINATE_RESULT

  return resultCase
}

const COMPUTE_ANALYSIS_RESULT_BY_PART: Record<
  FfsPart,
  ComputeAnalysisResultFn
> = {
  [FfsPart.PART_4]: ({
    mawpr,
    mawpDesign,
    finalAcceptabilityCriteriaCheckProps,
  }) => {
    if (!finalAcceptabilityCriteriaCheckProps) {
      return DEFAULT_INDETERMINATE_RESULT
    }

    const { failingCriteria } = finalAcceptabilityCriteriaCheckProps

    // pass scenario
    if (failingCriteria.size === 0) {
      return getFfsResultCase(FfsPartFourResultIds.PASS, FfsPart.PART_4)
    }

    // Check if thickness criteria is failing
    if (failingCriteria.has('thickness')) {
      return getFfsResultCase(
        FfsPartFourResultIds.FAIL_THICKNESS_CRITERIA,
        FfsPart.PART_4
      )
    }

    // Check if mawp criteria is failing
    if (failingCriteria.has('mawp')) {
      return getFfsResultCase(
        FfsPartFourResultIds.FAIL_MAWP_CRITERIA,
        FfsPart.PART_4
      )
    }

    // Check if missing information
    if (!mawpDesign || !mawpr) {
      return getFfsResultCase(
        FfsPartFourResultIds.FAIL_MISSING_INFORMATION,
        FfsPart.PART_4
      )
    }

    // Fallback case
    return getFfsResultCase(INDETERMINATE_FFS_RESULT_ID, FfsPart.PART_4)
  },
  [FfsPart.PART_5]: ({ mawpr, mawpDesign, rsf, flawSizeCheckResult }) => {
    if (!flawSizeCheckResult) {
      return DEFAULT_INDETERMINATE_RESULT
    }

    const { failingCriteria } = flawSizeCheckResult

    // Check if calculation fails due to flaw size criteria
    if (failingCriteria.size > 0) {
      const failedCriteriaNames = Array.from(failingCriteria).map(
        (criteria) => {
          switch (criteria) {
            case 'remainingThicknessRatio':
              return 'Remaining thickness ratio'
            case 'min_thickness':
              return 'Minimum thickness'
            case 'lmsd':
              return 'Location criteria (LMSD)'
            default:
              return criteria
          }
        }
      )

      const resultCase = getFfsResultCase(
        FfsPartFiveResultIds.FAIL_FLAW_SIZE,
        FfsPart.PART_5
      )
      return {
        ...resultCase,
        failedCriteria: failedCriteriaNames,
      }
    }

    // If no design pressure available, we can't make a comparison
    if (!mawpDesign || !mawpr) {
      const resultCase = getFfsResultCase(
        FfsPartFiveResultIds.FAIL_MISSING_DATA,
        FfsPart.PART_5
      )
      return resultCase
    }

    const mawprValue = mawpr.convertTo(mawpDesign.unit).num
    const designValue = mawpDesign.num
    const rsfValue = rsf?.convertTo(GeckoMeasurementUnit.Ratio)?.num ?? 0

    // MAWPr < MAWP (design MAWP) = Derate
    if (mawprValue < designValue) {
      const resultCase = getFfsResultCase(
        FfsPartFiveResultIds.DERATE,
        FfsPart.PART_5
      )
      return resultCase
    }

    // MAWPr > MAWP (design MAWP) & RSF > 0.9 = Pass
    if (mawprValue > designValue && rsfValue > 0.9) {
      const resultCase = getFfsResultCase(
        FfsPartFiveResultIds.PASS_HIGH_RSF,
        FfsPart.PART_5
      )
      return resultCase
    }

    // MAWPr > MAWP (design MAWP) & RSF < 0.9 = Pass with warning
    if (mawprValue > designValue && rsfValue < 0.9) {
      const resultCase = getFfsResultCase(
        FfsPartFiveResultIds.PASS_LOW_RSF,
        FfsPart.PART_5
      )
      return resultCase
    }

    // indeterminate case
    return DEFAULT_INDETERMINATE_RESULT
  },
}

// - - - - - - - - - - - - - - - - Result - Serialization - - - - - - - - - - - - - - - -
export type SerializePartSpecificDataFn = (
  report: FfsLtaReport
) => Record<string, Record<string, { num: number; unit: string } | null>>

const SERIALIZE_PART_SPECIFIC_DATA_BY_PART: Record<
  FfsPart,
  SerializePartSpecificDataFn
> = {
  [FfsPart.PART_4]: (report) => {
    const partFourReport = report as FfsPartFourReport

    if (partFourReport.ffs_part !== FfsPart.PART_4) {
      throw new Error('Report is not a Part 4 report')
    }
    return {
      thickness_criteria: transformMeasurementObject(
        partFourReport.thickness_criteria as FfsPartFourStoredThicknessCriteria &
          Record<string, GeckoMeasurement | null>
      ),
      calculated_values: transformMeasurementObject(
        partFourReport.calculated_values as FfsPartFourStoredCalculatedValues &
          Record<string, GeckoMeasurement | null>
      ),
    }
  },
  [FfsPart.PART_5]: (report) => {
    const partFiveReport = report as FfsPartFiveReport

    if (partFiveReport.ffs_part !== FfsPart.PART_5) {
      throw new Error('Report is not a Part 5 report')
    }
    return {
      final_acceptability_criteria: transformMeasurementObject(
        partFiveReport.final_acceptability_criteria as FfsPartFiveFinalAcceptabilityCriteria &
          Record<string, GeckoMeasurement | null>
      ),
      flaw_size_criteria: transformMeasurementObject(
        partFiveReport.flaw_size_criteria as FfsPartFiveStoredFlawSizeCriteria &
          Record<string, GeckoMeasurement | null>
      ),
    }
  },
}

// - - - - - - - - - - - - - - - - Result - Deserialization - - - - - - - - - - - - - - - -
type JsonMeasurementRecord = Record<
  string,
  { num: number; unit: string } | null
>

type DeserializePartSpecificDataFn = (
  data: Record<string, unknown>
) => FfsPartSpecificReportData

const DESERIALIZE_PART_SPECIFIC_DATA_BY_PART: Record<
  FfsPart,
  DeserializePartSpecificDataFn
> = {
  [FfsPart.PART_4]: (data): FfsPartSpecificReportData => {
    const thickness_criteria = transformJsonMeasurementObject(
      data.thickness_criteria as JsonMeasurementRecord
    ) as Record<string, GeckoMeasurement | null> &
      FfsPartFourStoredThicknessCriteria

    const calculated_values = transformJsonMeasurementObject(
      data.calculated_values as JsonMeasurementRecord
    ) as Record<string, GeckoMeasurement | null> &
      FfsPartFourStoredCalculatedValues

    return {
      thickness_criteria,
      calculated_values,
    }
  },
  [FfsPart.PART_5]: (data): FfsPartSpecificReportData => {
    const final_acceptability_criteria = transformJsonMeasurementObject(
      data.final_acceptability_criteria as JsonMeasurementRecord
    ) as Record<string, GeckoMeasurement | null> &
      FfsPartFiveFinalAcceptabilityCriteria

    const flaw_size_criteria = transformJsonMeasurementObject(
      data.flaw_size_criteria as JsonMeasurementRecord
    ) as Record<string, GeckoMeasurement | null> &
      FfsPartFiveStoredFlawSizeCriteria

    return {
      final_acceptability_criteria,
      flaw_size_criteria,
    }
  },
}

// - - - - - - - - - - - - - - - - Report Data - - - - - - - - - - - - - - - -
// Input arguments for creating part-specific report data
export interface PartSpecificReportDataArgs {
  // Part 5 specific
  flawSizeCriteriaCheck?: FlawSizeCriteriaCheckResult
  finalAcceptabilityCriteriaCheck?: FfsFinalAcceptabilityCriteraSectionValues
  // Part 4 specific
  calculatedValues?: FfsPartFourCalculatedValues
  thicknessCriteria?: Omit<FfsThicknessCriteriaSectionProps, 'loading'>
}

type FfsPartFourSpecificReportData = {
  thickness_criteria: FfsPartFourStoredThicknessCriteria
  calculated_values: FfsPartFourStoredCalculatedValues
}

type FfsPartFiveSpecificReportData = {
  final_acceptability_criteria: FfsPartFiveFinalAcceptabilityCriteria
  flaw_size_criteria: FfsPartFiveStoredFlawSizeCriteria
}

export type FfsPartSpecificReportData =
  | FfsPartFourSpecificReportData
  | FfsPartFiveSpecificReportData

type GetPartSpecificReportDataFn = (
  args: PartSpecificReportDataArgs
) => FfsPartSpecificReportData

const GET_PART_SPECIFIC_DATA_BY_PART: Record<
  FfsPart,
  GetPartSpecificReportDataFn
> = {
  [FfsPart.PART_4]: ({
    calculatedValues,
    thicknessCriteria,
  }): FfsPartSpecificReportData => ({
    thickness_criteria: thicknessCriteria
      ? {
          t_lim: thicknessCriteria.tLim,
          t_c_min: thicknessCriteria.tCMin,
          t_l_min: thicknessCriteria.tLMin,
          thickness_criteria_min: thicknessCriteria.thicknessCriteriaMin,
          effective_measured_thickness:
            thicknessCriteria.effectiveMeasuredThickness,
        }
      : {
          t_lim: null,
          t_c_min: null,
          t_l_min: null,
          thickness_criteria_min: null,
          effective_measured_thickness: null,
        },
    calculated_values: calculatedValues
      ? {
          rt: calculatedValues.rt,
          rsf_a: calculatedValues.rsfa,
          q: calculatedValues.q,
          l: calculatedValues.l,
          ts_am: calculatedValues.ts_am,
          tc_am: calculatedValues.tc_am,
        }
      : {
          rt: null,
          rsf_a: null,
          q: null,
          l: null,
          ts_am: null,
          tc_am: null,
        },
  }),
  [FfsPart.PART_5]: ({
    flawSizeCriteriaCheck,
    finalAcceptabilityCriteriaCheck,
  }): FfsPartSpecificReportData => ({
    final_acceptability_criteria: finalAcceptabilityCriteriaCheck
      ? {
          rsf: finalAcceptabilityCriteriaCheck.rsf,
          rsf_a: finalAcceptabilityCriteriaCheck.rsfa,
          mt: finalAcceptabilityCriteriaCheck.foliasFactor,
          rt_threshold: finalAcceptabilityCriteriaCheck.rtThreshold,
        }
      : {
          rsf: null,
          rsf_a: null,
          mt: null,
          rt_threshold: null,
        },
    flaw_size_criteria: flawSizeCriteriaCheck
      ? {
          rt: flawSizeCriteriaCheck.criteriaValues.remainingThicknessRatio,
          tmm_threshold: flawSizeCriteriaCheck.lossThreshold,
          lmsd: flawSizeCriteriaCheck.criteriaValues.lmsd,
          lmsd_threshold: flawSizeCriteriaCheck.lmsdThreshold,
          remainingThicknessThresholdRatio:
            flawSizeCriteriaCheck.remainingThicknessThreshold,
        }
      : {
          rt: null,
          tmm_threshold: null,
          lmsd: null,
          lmsd_threshold: null,
          remainingThicknessThresholdRatio: null,
        },
  }),
}

// - - - - - - - - - - - - - - - - Strategy Context - - - - - - - - - - - - - - - -
interface ScenarioMatchArgs {
  scenario: FfsLtaReport
  selectedScenarioId: string | null
  ffsLevel: FfsLevel
}

interface ScenarioDefaultArgs {
  scenario: FfsLtaReport
  ffsLevel: FfsLevel
}

export interface ScenarioValues {
  rsf_a: GeckoMeasurement | null
  e: GeckoMeasurement | null
  y: GeckoMeasurement | null
  w: GeckoMeasurement | null
  s_y: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  selectedMawpCalcThickness: MawpCalcThicknessOption
  calcType: MawpCalcType | null
}

export interface ThicknessOption {
  value: MawpCalcThicknessOption
  label: ReactNode
}

export type MawpCalcType = 'thinWall' | 'thickWall'

export interface CalcTypeOption {
  value: MawpCalcType
  label: string
}

export interface ComputeMawpProps {
  t: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  yieldStrength: GeckoMeasurement | null
  qualityFactor: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  axis: 'x' | 'y'
  // Piping-specific (optional for PV)
  pressureCoefficient?: GeckoMeasurement | null
  weldStrengthReduction?: GeckoMeasurement | null
  // PV-specific (optional for Piping)
  tNom?: GeckoMeasurement | null
  calcType?: MawpCalcType | null
}

export interface ComputeThicknessFromMawpProps {
  mawp: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  yieldStrength: GeckoMeasurement | null
  qualityFactor: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  axis: 'x' | 'y'
  // Piping-specific (optional for PV)
  pressureCoefficient?: GeckoMeasurement | null
  weldStrengthReduction?: GeckoMeasurement | null
  // PV-specific (optional for Piping)
  tNom?: GeckoMeasurement | null
  calcType?: MawpCalcType | null
}

export type ComputeMawpFn = (args: ComputeMawpProps) => GeckoMeasurement | null

type ComputeThicknessFromMawpFn = (
  args: ComputeThicknessFromMawpProps
) => GeckoMeasurement | null

interface FfsStrategy {
  normalizeDataValues: (
    args: NormalizeDataValuesArgs
  ) => NormalizedFfsDataValues | null
  mawp: {
    mawpCalcThicknessOptions: readonly ThicknessOption[]
    mawpCalcTypeOptions: readonly CalcTypeOption[]
    computeMawp: ComputeMawpFn | null
    computeThicknessFromMawp: ComputeThicknessFromMawpFn | null
    computeReratedMawp: ComputeReratedMawpFn | null
    renderReratedTooltip: (ffsLevel: FfsLevel) => ReactNode
    renderCircumferentialTooltip: () => ReactNode
    renderLongitudinalTooltip: () => ReactNode
  }
  scenarios: {
    isSelectedScenario: (args: ScenarioMatchArgs) => boolean
    isDefaultScenario: (args: ScenarioDefaultArgs) => boolean
    filterScenarios: (
      scenarios: ReadonlyArray<FfsLtaReport>,
      ffsLevel: FfsLevel
    ) => ReadonlyArray<FfsLtaReport>
    getScenarioValues: (scenario: FfsLtaReport) => ScenarioValues
  }
  analysisResult: {
    criteriaThicknessThreshold: GeckoMeasurement
    getFfsResultCases: () => readonly FfsAnalysisResult[]
    computeAnalysisResult: (
      args: ComputeAnalysisResultArgs
    ) => FfsAnalysisResult
  }
  report: {
    getPartSpecificReportData: (
      args: PartSpecificReportDataArgs
    ) => FfsPartSpecificReportData
    serializePartSpecificData: (
      report: FfsLtaReport
    ) => Record<string, Record<string, { num: number; unit: string } | null>>
    deserializePartSpecificData: (
      data: Record<string, unknown>
    ) => FfsPartSpecificReportData
  }
}

interface FfsStrategyContextValue {
  ffsPart: FfsPart
  strategy: FfsStrategy
}

export interface FfsStrategyProviderProps {
  ffsPart: FfsPart
  componentModelType: GeckoComponentModelType | undefined
  children: ReactNode
}

const FfsStrategyContext = createContext<FfsStrategyContextValue | undefined>(
  undefined
)

export function createFfsStrategy(
  ffsPart: FfsPart,
  componentModelType: GeckoComponentModelType | undefined
): FfsStrategy {
  const isSelectedScenario = ({
    scenario,
    selectedScenarioId,
    ffsLevel,
  }: ScenarioMatchArgs): boolean =>
    scenario.id === selectedScenarioId &&
    scenario.ffs_level === ffsLevel &&
    scenario.ffs_part === ffsPart

  const isDefaultScenario = ({
    scenario,
    ffsLevel,
  }: ScenarioDefaultArgs): boolean =>
    scenario.is_default === true &&
    scenario.ffs_level === ffsLevel &&
    scenario.ffs_part === ffsPart

  const filterScenarios = (
    scenarios: ReadonlyArray<FfsLtaReport>,
    ffsLevel: FfsLevel
  ): ReadonlyArray<FfsLtaReport> =>
    filter(scenarios, {
      ffs_level: ffsLevel,
      ffs_part: ffsPart,
    })

  const getScenarioValues = (scenario: FfsLtaReport): ScenarioValues => {
    let rsf_a: GeckoMeasurement | null = null

    if (ffsPart === FfsPart.PART_4) {
      const partFourReport = scenario as FfsPartFourReport
      rsf_a = partFourReport.calculated_values.rsf_a
    } else {
      const partFiveReport = scenario as FfsPartFiveReport
      rsf_a = partFiveReport.final_acceptability_criteria.rsf_a
    }

    return {
      rsf_a,
      e: scenario.mawp.e,
      y: scenario.mawp.y,
      w: scenario.mawp.w,
      s_y: scenario.mawp.s_y,
      tsl: scenario.mawp.tsl,
      fca: scenario.user_params.fca,
      fcaMl: scenario.user_params.fcaMl,
      selectedMawpCalcThickness: scenario.user_params.selectedMawpCalcThickness,
      calcType: scenario.mawp.calcType,
    }
  }

  const computeMawp = (args: ComputeMawpProps): GeckoMeasurement | null => {
    if (isNil(componentModelType)) {
      return null
    }

    // Dispatch to the appropriate function based on component type
    if (componentModelType === GeckoComponentModelType.PipeSection) {
      // Piping requires pressureCoefficient
      if (isNil(args.pressureCoefficient)) {
        return null
      }
      return computePipeSectionMAWP({
        t: args.t,
        tsl: args.tsl,
        yieldStrength: args.yieldStrength,
        qualityFactor: args.qualityFactor,
        outerDiameter: args.outerDiameter,
        pressureCoefficient: args.pressureCoefficient,
        weldStrengthReduction: args.weldStrengthReduction,
        axis: args.axis,
      })
    } else if (
      componentModelType === GeckoComponentModelType.PressureVesselShell
    ) {
      // PV requires tNom and calcType
      if (isNil(args.tNom) || isNil(args.calcType)) {
        return null
      }
      return computePVCylindricalShellMAWP({
        t: args.t,
        tsl: args.tsl,
        tNom: args.tNom,
        yieldStrength: args.yieldStrength,
        qualityFactor: args.qualityFactor,
        outerDiameter: args.outerDiameter,
        axis: args.axis,
        calcType: args.calcType,
      })
    }

    return null
  }

  const computeThicknessFromMawp = (
    args: ComputeThicknessFromMawpProps
  ): GeckoMeasurement | null => {
    if (isNil(componentModelType)) {
      return null
    }

    // Dispatch to the appropriate function based on component type
    if (componentModelType === GeckoComponentModelType.PipeSection) {
      // Piping requires pressureCoefficient
      if (isNil(args.pressureCoefficient)) {
        return null
      }
      return computePipeSectionThicknessFromMAWP({
        mawp: args.mawp,
        tsl: args.tsl,
        yieldStrength: args.yieldStrength,
        qualityFactor: args.qualityFactor,
        outerDiameter: args.outerDiameter,
        pressureCoefficient: args.pressureCoefficient,
        weldStrengthReduction: args.weldStrengthReduction,
        axis: args.axis,
      })
    } else if (
      componentModelType === GeckoComponentModelType.PressureVesselShell
    ) {
      // PV requires tNom and calcType
      if (isNil(args.tNom) || isNil(args.calcType)) {
        return null
      }
      return computePVCylindricalShellThicknessFromMAWP({
        mawp: args.mawp,
        tsl: args.tsl,
        tNom: args.tNom,
        yieldStrength: args.yieldStrength,
        qualityFactor: args.qualityFactor,
        outerDiameter: args.outerDiameter,
        axis: args.axis,
        calcType: args.calcType,
      })
    }

    return null
  }

  return {
    normalizeDataValues: NORMALIZE_DATA_VALUES_BY_PART[ffsPart],
    mawp: {
      mawpCalcThicknessOptions: MAWP_CALC_THICKNESS_OPTIONS_BY_PART[ffsPart],
      mawpCalcTypeOptions: getMawpCalcTypeOptions(componentModelType),
      computeMawp: computeMawp,
      computeThicknessFromMawp: computeThicknessFromMawp,
      computeReratedMawp: COMPUTE_RERATED_MAWP_BY_PART[ffsPart],
      renderReratedTooltip: RENDER_RERATED_TOOLTIP_BY_PART[ffsPart],
      renderCircumferentialTooltip: RENDER_CIRC_MAWP_TOOLTIP_BY_PART[ffsPart],
      renderLongitudinalTooltip: RENDER_LONG_MAWP_TOOLTIP_BY_PART[ffsPart],
    },
    scenarios: {
      isSelectedScenario,
      isDefaultScenario,
      filterScenarios,
      getScenarioValues,
    },
    analysisResult: {
      criteriaThicknessThreshold: getThicknessThreshold(componentModelType),
      getFfsResultCases: () =>
        ffsPart === FfsPart.PART_4
          ? FFS_RESULT_CASES_PART_4
          : FFS_RESULT_CASES_PART_5,
      computeAnalysisResult: COMPUTE_ANALYSIS_RESULT_BY_PART[ffsPart],
    },
    report: {
      getPartSpecificReportData: GET_PART_SPECIFIC_DATA_BY_PART[ffsPart],
      serializePartSpecificData: SERIALIZE_PART_SPECIFIC_DATA_BY_PART[ffsPart],
      deserializePartSpecificData:
        DESERIALIZE_PART_SPECIFIC_DATA_BY_PART[ffsPart],
    },
  }
}

export const FfsStrategyProvider: FC<FfsStrategyProviderProps> = ({
  ffsPart,
  componentModelType,
  children,
}) => {
  const strategy = useMemo(
    () => createFfsStrategy(ffsPart, componentModelType),
    [ffsPart, componentModelType]
  )
  const value = useMemo<FfsStrategyContextValue>(
    () => ({
      ffsPart,
      strategy,
    }),
    [ffsPart, strategy]
  )

  return (
    <FfsStrategyContext.Provider value={value}>
      {children}
    </FfsStrategyContext.Provider>
  )
}

export function useFfsStrategy(): FfsStrategyContextValue {
  const context = useContext(FfsStrategyContext)
  if (!context) {
    throw new Error('useFfsStrategy must be used within an FfsStrategyProvider')
  }
  return context
}
